// src/components/Dashboard/CoachDashboard/RSVPHealthWidget.jsx
// Shows RSVP status for the next upcoming event

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAuth } from 'firebase/auth';
import { 
  FaChartPie, FaCheckCircle, FaTimesCircle, FaQuestionCircle,
  FaBell, FaArrowRight, FaExclamationTriangle
} from 'react-icons/fa';
import styles from './RSVPHealthWidget.module.css';

const RSVPHealthWidget = ({ program, refreshTrigger }) => {
  const navigate = useNavigate();
  const [nextEvent, setNextEvent] = useState(null);
  const [rsvpSummary, setRsvpSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  // Fetch next upcoming event
  useEffect(() => {
    const fetchNextEvent = async () => {
      if (!program?._id) {
        setNextEvent(null);
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';
        const auth = getAuth();
        const token = auth.currentUser ? await auth.currentUser.getIdToken() : null;
        
        const headers = token ? {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        } : {};
        
        const response = await fetch(`${backendUrl}/api/events/${program._id}`, { headers });
        
        if (!response.ok) {
          setNextEvent(null);
          return;
        }

        const events = await response.json();
        const now = new Date();
        
        // Find next upcoming event (use dateTime.start)
        const upcoming = events
          .filter(e => new Date(e.dateTime?.start) > now && e.status !== 'Cancelled')
          .sort((a, b) => new Date(a.dateTime?.start) - new Date(b.dateTime?.start))[0];

        setNextEvent(upcoming || null);
      } catch (error) {
        console.error('Error fetching next event:', error);
        setNextEvent(null);
      } finally {
        setLoading(false);
      }
    };

    fetchNextEvent();
  }, [program?._id, refreshTrigger]);

  // Fetch RSVP data for next event
  useEffect(() => {
    const fetchRsvpData = async () => {
      if (!nextEvent?._id) {
        setRsvpSummary(null);
        return;
      }

      try {
        const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';
        const auth = getAuth();
        const token = auth.currentUser ? await auth.currentUser.getIdToken() : null;
        
        if (!token) return;

        const response = await fetch(`${backendUrl}/api/events/response/${nextEvent._id}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        
        if (response.ok) {
          const data = await response.json();
          setRsvpSummary(data.summary || null);
        }
      } catch (error) {
        console.error('Error fetching RSVP data:', error);
      }
    };

    fetchRsvpData();
  }, [nextEvent?._id]);

  const formatEventDate = (event) => {
    const date = new Date(event?.dateTime?.start);
    const now = new Date();
    const tomorrow = new Date(now);
    tomorrow.setDate(tomorrow.getDate() + 1);
    
    if (date.toDateString() === now.toDateString()) {
      return 'Today';
    } else if (date.toDateString() === tomorrow.toDateString()) {
      return 'Tomorrow';
    }
    return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  };

  const getRSVPMetrics = () => {
    if (!rsvpSummary) {
      return { going: 0, notGoing: 0, tbd: 0, pending: 0, total: 0 };
    }
    
    const going = rsvpSummary.going || 0;
    const notGoing = rsvpSummary.notGoing || 0;
    const tbd = rsvpSummary.tbd || 0;
    const total = rsvpSummary.total || (going + notGoing + tbd);
    const pending = total - going - notGoing - tbd;
    
    return { going, notGoing, tbd, pending: pending > 0 ? pending : 0, total };
  };

  const handleSendReminder = () => {
    // TODO: Implement send reminder functionality
    // TODO: Implement RSVP reminder
  };

  const handleViewEvent = () => {
    if (nextEvent && program) {
      navigate(`/program/${program._id}/events?eventId=${nextEvent._id}`);
    }
  };

  const metrics = getRSVPMetrics();
  const rsvpRate = metrics.total > 0 
    ? Math.round(((metrics.going + metrics.notGoing + metrics.tbd) / metrics.total) * 100) 
    : 0;

  // Loading state
  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.header}>
          <FaChartPie className={styles.headerIcon} />
          <h3 className={styles.headerTitle}>RSVP Health</h3>
        </div>
        <div className={styles.loadingState}>
          <div className={styles.loadingSpinner}></div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <FaChartPie className={styles.headerIcon} />
        <h3 className={styles.headerTitle}>RSVP Health</h3>
      </div>

      <div className={styles.content}>
        {!nextEvent ? (
          <div className={styles.emptyState}>
            <span className={styles.emptyIcon}>📅</span>
            <p>No upcoming events</p>
          </div>
        ) : (
          <>
            {/* Next Event Info */}
            <div className={styles.eventInfo}>
              <span className={styles.eventLabel}>Next Event:</span>
              <span className={styles.eventName}>{nextEvent.title}</span>
              <span className={styles.eventDate}>({formatEventDate(nextEvent)})</span>
            </div>

            {/* RSVP Breakdown */}
            <div className={styles.rsvpBreakdown}>
              <div className={styles.rsvpItem}>
                <FaCheckCircle className={`${styles.rsvpIcon} ${styles.going}`} />
                <span className={styles.rsvpLabel}>Going</span>
                <span className={styles.rsvpValue}>{metrics.going}</span>
              </div>
              <div className={styles.rsvpItem}>
                <FaTimesCircle className={`${styles.rsvpIcon} ${styles.notGoing}`} />
                <span className={styles.rsvpLabel}>Not Going</span>
                <span className={styles.rsvpValue}>{metrics.notGoing}</span>
              </div>
              <div className={styles.rsvpItem}>
                <FaQuestionCircle className={`${styles.rsvpIcon} ${styles.maybe}`} />
                <span className={styles.rsvpLabel}>Maybe</span>
                <span className={styles.rsvpValue}>{metrics.tbd}</span>
              </div>
            </div>

            {/* Pending Warning */}
            {metrics.pending > 0 && (
              <div className={styles.pendingWarning}>
                <FaExclamationTriangle className={styles.warningIcon} />
                <span>{metrics.pending} haven't responded</span>
                <button className={styles.reminderBtn} onClick={handleSendReminder}>
                  <FaBell /> Remind
                </button>
              </div>
            )}

            {/* RSVP Rate */}
            <div className={styles.rateSection}>
              <div className={styles.rateBar}>
                <div 
                  className={styles.rateFill} 
                  style={{ width: `${rsvpRate}%` }}
                ></div>
              </div>
              <span className={styles.rateLabel}>
                {rsvpRate}% Response Rate
              </span>
            </div>
          </>
        )}
      </div>

      {nextEvent && (
        <div className={styles.footer}>
          <button className={styles.footerLink} onClick={handleViewEvent}>
            View Event Details <FaArrowRight />
          </button>
        </div>
      )}
    </div>
  );
};

export default RSVPHealthWidget;

